import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { App, Button, Input, Select } from "antd";
import theme from "../../themes";
import { useAppDispatch, useAppSelector } from "../../store";
import { getAgentsThunk } from "../../store/actions/agent.action";
import { getCompanyInfoThunk } from "../../store/actions/company.action";
import { updateCompanySip } from "../../services/company.service";
import { IAgent, resolveAgentChannels } from "../../models/agent.model";
import { ROLES } from "../../models/user.model";
import ToastMessage from "../../utils/toastMessage";

const UNASSIGNED = "";

const errorText = (error: unknown, fallback: string) => {
  if (typeof error === "string" && error.trim()) return error;
  if (error && typeof error === "object" && "response" in error) {
    const message = (error as { response?: { data?: { message?: string } } }).response
      ?.data?.message;
    if (message) return message;
  }
  return fallback;
};

const SipSettings: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { message } = App.useApp();
  const toast = ToastMessage(message);
  const { user } = useAppSelector((state) => state.auth);
  const sip = useAppSelector((state) => state.company.info?.settings?.sip);
  const canEdit = user?.role === ROLES.ADMIN;
  const [agents, setAgents] = useState<IAgent[]>([]);
  const [domain, setDomain] = useState("");
  const [agentId, setAgentId] = useState(UNASSIGNED);
  const [frontDesk, setFrontDesk] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDomain(sip?.domain || "");
    setAgentId(sip?.agent_id || UNASSIGNED);
    setFrontDesk(sip?.front_desk_uri || "");
  }, [sip]);

  useEffect(() => {
    dispatch(
      getAgentsThunk({
        limit: 999,
        page: 1,
        keyword: "",
        direction: "inbound",
      })
    )
      .unwrap()
      .then((res) =>
        setAgents(
          (res.data.agents || []).filter((agent: IAgent) =>
            resolveAgentChannels(agent.channels).includes("call")
          )
        )
      )
      .catch(() => undefined);
  }, [dispatch]);

  const save = async () => {
    setSaving(true);
    try {
      await updateCompanySip({
        domain: domain.trim(),
        agent_id: agentId,
        front_desk_uri: frontDesk.trim(),
      });
      await dispatch(getCompanyInfoThunk()).unwrap();
      toast.success(t("settings.sip.saved"));
    } catch (error) {
      toast.error(errorText(error, t("settings.sip.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  const usesAgent = agentId !== UNASSIGNED;

  return (
    <Stack>
      <Hint>{t("settings.sip.hint")}</Hint>
      <Panel>
        <Field>
          <Label>{t("settings.sip.domain")}</Label>
          <Input
            value={domain}
            disabled={!canEdit}
            placeholder="lytte-dev.sip.twilio.com"
            onChange={(event) => setDomain(event.target.value)}
          />
          <FieldHint>{t("settings.sip.domainHint")}</FieldHint>
        </Field>
        <Field>
          <Label>{t("settings.sip.agent")}</Label>
          <Select
            value={agentId}
            disabled={!canEdit}
            onChange={setAgentId}
            options={[
              { value: UNASSIGNED, label: t("settings.sip.noAgent") },
              ...agents.map((agent) => ({
                value: agent._id,
                label: agent.agent_name,
              })),
            ]}
          />
          <FieldHint>
            {usesAgent ? t("settings.sip.agentHint") : t("settings.sip.noAgentHint")}
          </FieldHint>
        </Field>
        <Field>
          <Label>{t("settings.sip.frontDesk")}</Label>
          <Input
            value={frontDesk}
            disabled={!canEdit}
            placeholder="sip:front-desk@lytte-dev.sip.twilio.com"
            onChange={(event) => setFrontDesk(event.target.value)}
          />
          <FieldHint>{t("settings.sip.frontDeskHint")}</FieldHint>
        </Field>
        {canEdit && (
          <Button type="primary" loading={saving} onClick={save}>
            {t("common.save")}
          </Button>
        )}
      </Panel>
    </Stack>
  );
};

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Hint = styled.p`
  margin: 0;
  max-width: 820px;
  font-size: 16px;
  line-height: 1.5;
  color: ${theme.colors.grey1};
`;

const Panel = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  max-width: 720px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.span`
  font-weight: 600;
`;

const FieldHint = styled.span`
  color: ${theme.colors.grey1};
  font-size: 13px;
  line-height: 1.4;
`;

export default SipSettings;
